import React from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Fingerprint,
  KeyRound,
  Loader2,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { AcShieldLogo } from './AcShieldLogo';

type BiometricGateAccount = {
  nome?: string;
  email?: string;
  fotoUrl?: string | null;
};

type BiometricGateViewProps = {
  mode: 'unlock' | 'enroll';
  title: string;
  subtitle: React.ReactNode;
  account?: BiometricGateAccount | null;
  loading?: boolean;
  success?: boolean;
  disabled?: boolean;
  error?: string;
  showErrorDetails?: boolean;
  onToggleErrorDetails?: () => void;
  unsupportedMessage?: React.ReactNode;
  primaryLabel: string;
  primaryLoadingLabel?: string;
  primarySuccessLabel?: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  logoutLabel?: string;
  onLogout?: () => void;
  children?: React.ReactNode;
};

export function BiometricGateView({
  mode,
  title,
  subtitle,
  account,
  loading = false,
  success = false,
  disabled = false,
  error,
  showErrorDetails = false,
  onToggleErrorDetails,
  unsupportedMessage,
  primaryLabel,
  primaryLoadingLabel = 'Aguardando biometria...',
  primarySuccessLabel = 'Acesso habilitado',
  onPrimary,
  secondaryLabel,
  onSecondary,
  logoutLabel,
  onLogout,
  children,
}: BiometricGateViewProps) {
  const accountInitials = getInitials(account?.nome);
  const shouldPromoteAccount = mode === 'enroll' && Boolean(account) && !success;
  const headline = shouldPromoteAccount ? account?.nome || title : title;
  const description = shouldPromoteAccount ? account?.email || subtitle : subtitle;

  return (
    <div style={gateOverlayStyle}>
      <div style={gateShellStyle}>
        <div style={brandRowStyle}>
          <div style={brandLogoStyle}>
            <AcShieldLogo />
          </div>
          <div>
            <div style={brandTitleStyle}>
              Aluno <span style={brandAccentStyle}>Conectado</span>
            </div>
            <div style={brandSubtitleStyle}>Ecossistema Educacional Seguro</div>
          </div>
        </div>

        <div style={gateCardStyle}>
          <div style={shouldPromoteAccount ? featuredIconContainerStyle : iconContainerStyle}>
            {shouldPromoteAccount && account?.fotoUrl ? (
              <img src={account.fotoUrl} alt={account.nome || 'Conta'} style={featuredAvatarStyle} />
            ) : shouldPromoteAccount ? (
              <div style={featuredAvatarFallbackStyle}>{accountInitials}</div>
            ) : success ? (
              <CheckCircle size={34} style={{ color: '#86efac' }} />
            ) : mode === 'unlock' ? (
              <ShieldCheck size={34} style={{ color: '#ddd6fe' }} />
            ) : (
              <Fingerprint size={34} style={{ color: '#ddd6fe' }} />
            )}
          </div>

          <h2 style={titleStyle}>{headline}</h2>
          <p style={subtitleStyle}>{description}</p>

          {account && !shouldPromoteAccount && (
            <div style={accountCardStyle}>
              {account.fotoUrl ? (
                <img src={account.fotoUrl} alt={account.nome || 'Conta'} style={avatarStyle} />
              ) : (
                <div style={avatarFallbackStyle}>{accountInitials}</div>
              )}
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={accountNameStyle}>{account.nome?.split(' ')[0] || 'Conta'}</div>
                {account.email && <div style={accountEmailStyle}>{account.email}</div>}
              </div>
            </div>
          )}

          {unsupportedMessage && <div style={warningStyle}>{unsupportedMessage}</div>}

          {error && (
            <div style={errorWrapperStyle}>
              {onToggleErrorDetails ? (
                <>
                  <button
                    type="button"
                    onClick={onToggleErrorDetails}
                    style={errorToggleStyle}
                    title="Ver detalhes do erro"
                  >
                    <AlertTriangle size={15} />
                    <span style={{ textDecoration: 'underline' }}>Ver detalhes do erro</span>
                  </button>
                  {showErrorDetails && <div style={errorDetailStyle}>{error}</div>}
                </>
              ) : (
                <div style={errorDetailStyle}>{error}</div>
              )}
            </div>
          )}

          {children ? (
            children
          ) : (
            <div style={{ width: '100%' }}>
              <button
                type="button"
                onClick={onPrimary}
                disabled={loading || success || disabled}
                style={{
                  ...primaryButtonStyle,
                  ...(success ? primaryButtonSuccessStyle : {}),
                  ...(loading || disabled ? primaryButtonDisabledStyle : {}),
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'pwa-spin 1s linear infinite' }} />
                    {primaryLoadingLabel}
                  </>
                ) : success ? (
                  primarySuccessLabel
                ) : (
                  <>
                    <Fingerprint size={22} />
                    {primaryLabel}
                  </>
                )}
              </button>

              {secondaryLabel && onSecondary && !success && (
                <button type="button" onClick={onSecondary} style={secondaryButtonStyle}>
                  <KeyRound size={15} />
                  {secondaryLabel}
                </button>
              )}
            </div>
          )}

          {logoutLabel && onLogout && (
            <button type="button" onClick={onLogout} style={logoutButtonStyle}>
              <LogOut size={15} />
              {logoutLabel}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pwa-spin { to { transform: rotate(360deg); } }
        @keyframes pwa-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pwa-purple-glow {
          0%, 100% { box-shadow: 0 22px 60px rgba(17, 7, 45, 0.65), 0 0 0 1px rgba(221, 214, 254, 0.12); }
          50% { box-shadow: 0 26px 72px rgba(88, 28, 135, 0.62), 0 0 0 1px rgba(221, 214, 254, 0.18); }
        }
      `}</style>
    </div>
  );
}

function getInitials(name?: string): string {
  if (!name?.trim()) return 'AC';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

const gateOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 999999,
  minHeight: '100dvh',
  width: '100vw',
  overflow: 'auto',
  background:
    'radial-gradient(circle at top, rgba(168,85,247,0.28) 0%, transparent 34%), linear-gradient(160deg, #13051f 0%, #251050 46%, #090313 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'max(18px, env(safe-area-inset-top)) 18px max(18px, env(safe-area-inset-bottom))',
  boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  animation: 'pwa-fade-in 0.25s ease-out',
};

const gateShellStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  minHeight: 'min(760px, 100dvh)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 26,
};

const brandRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 14,
  color: '#ffffff',
};

const brandLogoStyle: React.CSSProperties = {
  width: 54,
  height: 54,
  flexShrink: 0,
};

const brandTitleStyle: React.CSSProperties = {
  fontSize: '1.45rem',
  fontWeight: 850,
  lineHeight: 1.08,
  letterSpacing: 0,
};

const brandAccentStyle: React.CSSProperties = {
  color: '#c4b5fd',
};

const brandSubtitleStyle: React.CSSProperties = {
  marginTop: 4,
  color: 'rgba(237, 233, 254, 0.7)',
  fontSize: '.84rem',
  fontWeight: 650,
  letterSpacing: 0,
};

const gateCardStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 24,
  padding: '30px 24px',
  background: 'linear-gradient(180deg, rgba(46,16,101,0.90) 0%, rgba(30,12,64,0.92) 100%)',
  border: '1px solid rgba(221, 214, 254, 0.14)',
  backdropFilter: 'blur(22px)',
  color: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  animation: 'pwa-purple-glow 3s ease-in-out infinite',
};

const iconContainerStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(124,58,237,0.36), rgba(76,29,149,0.48))',
  border: '1px solid rgba(221, 214, 254, 0.22)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 18,
};

const featuredIconContainerStyle: React.CSSProperties = {
  ...iconContainerStyle,
  width: 96,
  height: 96,
  overflow: 'hidden',
  padding: 5,
  boxSizing: 'border-box',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.32rem',
  fontWeight: 850,
  color: '#ffffff',
  margin: 0,
  letterSpacing: 0,
};

const subtitleStyle: React.CSSProperties = {
  color: '#ddd6fe',
  fontSize: '.92rem',
  lineHeight: 1.55,
  fontWeight: 560,
  margin: '10px 0 20px',
};

const accountCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 16,
  padding: '11px 13px',
  marginBottom: 18,
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(221,214,254,0.14)',
};

const avatarStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
};

const featuredAvatarStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
};

const featuredAvatarFallbackStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a855f7, #4c1d95)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.55rem',
  fontWeight: 900,
  letterSpacing: 0,
};

const avatarFallbackStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #7c3aed, #4c1d95)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '.82rem',
  fontWeight: 850,
  flexShrink: 0,
};

const accountNameStyle: React.CSSProperties = {
  color: '#ffffff',
  fontSize: '.92rem',
  fontWeight: 800,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const accountEmailStyle: React.CSSProperties = {
  color: '#c4b5fd',
  fontSize: '.74rem',
  fontWeight: 560,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const warningStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: 16,
  padding: '12px 13px',
  borderRadius: 14,
  color: '#fde68a',
  background: 'rgba(251,191,36,0.10)',
  border: '1px solid rgba(251,191,36,0.22)',
  fontSize: '.78rem',
  lineHeight: 1.45,
  textAlign: 'left',
};

const errorWrapperStyle: React.CSSProperties = {
  width: '100%',
  margin: '0 0 16px',
};

const errorToggleStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#c4b5fd',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  fontSize: '.78rem',
  fontWeight: 700,
  padding: 0,
};

const errorDetailStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: 13,
  color: '#fecaca',
  background: 'rgba(248,113,113,0.10)',
  border: '1px solid rgba(248,113,113,0.18)',
  fontSize: '.76rem',
  lineHeight: 1.45,
  textAlign: 'left',
};

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 54,
  padding: '15px 18px',
  borderRadius: 16,
  border: 'none',
  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #581c87 100%)',
  color: '#ffffff',
  fontSize: '.96rem',
  fontWeight: 850,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  boxShadow: '0 5px 0 #3b0764, 0 16px 28px rgba(88,28,135,0.42)',
  letterSpacing: 0,
};

const primaryButtonSuccessStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
  boxShadow: '0 5px 0 #14532d, 0 16px 28px rgba(21,128,61,0.30)',
};

const primaryButtonDisabledStyle: React.CSSProperties = {
  opacity: 0.72,
  cursor: 'not-allowed',
  transform: 'none',
};

const secondaryButtonStyle: React.CSSProperties = {
  marginTop: 15,
  background: 'none',
  border: 'none',
  color: '#ddd6fe',
  fontSize: '.8rem',
  fontWeight: 750,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
};

const logoutButtonStyle: React.CSSProperties = {
  marginTop: 26,
  background: 'none',
  border: 'none',
  color: '#c4b5fd',
  fontSize: '.79rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
};
